import {
  type ColumnHelper,
  flexRender,
  type Header,
  type HeaderGroup,
  type Row,
  type RowData,
  type TableOptions,
  useTable as useTanStackTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { cloneElement, createContext, type JSX, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Icon } from 'ui-icons';
import { ScrollAreaV2 } from '../ScrollArea/ScrollArea';
import type { MarbleTableFeatures } from './features';
import { marbleTableFeatures } from './features';

const WithRowLinkContext = createContext(false);

function TableContainer({
  className,
  scrollElementRef,
  withRowLink,
  ...props
}: React.ComponentProps<'table'> & {
  scrollElementRef: React.RefObject<HTMLDivElement | null>;
  withRowLink: boolean;
}) {
  return (
    <ScrollAreaV2
      ref={scrollElementRef}
      className={clsx('border-grey-border border-spacing-0 rounded-lg border', className)}
      orientation="both"
      type="auto"
    >
      <WithRowLinkContext.Provider value={withRowLink}>
        <table className="isolate w-full table-fixed border-separate border-spacing-0" {...props} />
      </WithRowLinkContext.Provider>
    </ScrollAreaV2>
  );
}

function TableTH<TData extends RowData, TValue>({
  header,
  children,
  className,
  style,
  ...props
}: React.ComponentProps<'th'> & { header: Header<MarbleTableFeatures, TData, TValue> }) {
  return (
    <th
      colSpan={header.colSpan}
      style={{ width: header.getSize(), ...style }}
      className={clsx(
        'border-grey-border bg-surface-card relative h-12 w-full border-b border-r px-md last:border-r-0',
        {
          'cursor-pointer select-none': header.column.getCanSort(),
        },
        className,
      )}
      onClick={header.column.getToggleSortingHandler()}
      {...props}
    >
      {children}
    </th>
  );
}

const internalRowLink = '__internal-row-link';

// biome-ignore lint/suspicious/noRedeclare: <TBD>
function Header<TData extends RowData>({
  headerGroups,
  actions,
}: {
  headerGroups: HeaderGroup<MarbleTableFeatures, TData>[];
  /** Rendered at the end of the last header cell, which grows so they never overflow */
  actions?: React.ReactNode;
}) {
  const [actionsMinWidth, setActionsMinWidth] = useState(0);
  const measureActions = useCallback((actionsElement: HTMLDivElement | null) => {
    if (!actionsElement) return;
    const th = actionsElement.closest('th');
    const observer = new ResizeObserver(() => {
      const thStyle = th ? getComputedStyle(th) : null;
      const thPadding = thStyle ? parseFloat(thStyle.paddingInlineStart) + parseFloat(thStyle.paddingInlineEnd) : 0;
      setActionsMinWidth(actionsElement.offsetWidth + thPadding);
    });
    observer.observe(actionsElement);
    return () => observer.disconnect();
  }, []);

  return (
    <thead className="sticky top-0 z-10">
      {headerGroups.map((headerGroup, groupIndex) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header, index) => {
            const context = header.getContext();
            const isLastHeader = groupIndex === headerGroups.length - 1 && index === headerGroup.headers.length - 1;
            if (header.id === internalRowLink) {
              return (
                <th
                  colSpan={header.colSpan}
                  key={`${header.id}-${index}`}
                  className="bg-surface-card border-grey-border w-0 border-b"
                ></th>
              );
            }
            return (
              <Table.TH
                header={header}
                key={header.id}
                style={isLastHeader && actions ? { width: Math.max(header.getSize(), actionsMinWidth) } : undefined}
              >
                {header.isPlaceholder ? null : (
                  <div className="text-s text-grey-primary flex flex-row items-center font-semibold">
                    <div className="flex flex-1">{flexRender(header.column.columnDef.header, context)}</div>
                    {{
                      asc: <Icon icon="arrow-2-up" className="size-6" />,
                      desc: <Icon icon="arrow-2-down" className="size-6" />,
                    }[header.column.getIsSorted() as string] ?? null}
                    {isLastHeader && actions ? (
                      <div ref={measureActions} className="flex shrink-0 items-center gap-sm whitespace-nowrap">
                        {actions}
                      </div>
                    ) : null}
                    {header.column.getCanResize() ? (
                      <div
                        className={clsx(
                          'hover:bg-grey-border active:bg-grey-placeholder absolute end-0 h-full w-1 cursor-col-resize touch-none select-none',
                          // Hack to take scroll bar into account
                          index === headerGroup.headers.length - 1 && 'end-sm',
                        )}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                        onDoubleClick={() => {
                          header.column.resetSize();
                        }}
                        aria-hidden="true"
                      />
                    ) : null}
                  </div>
                )}
              </Table.TH>
            );
          })}
        </tr>
      ))}
    </thead>
  );
}

interface TableProps<TData extends RowData>
  extends Omit<TableOptions<MarbleTableFeatures, TData>, 'features' | 'columns'> {
  columns: ReturnType<ColumnHelper<MarbleTableFeatures, TData>['columns']>;
  /**
   * Transform the row into a link :
   * - the link will be placed in a dedicated first column with width = 0.
   * - the row will be clickable using the [redundant click event](https://inclusive-components.design/cards/#theredundantclickevent) pattern
   * - the row will be highlighted on hover / focus-within (= when the link is focused).
   */
  rowLink?: (row: TData) => JSX.Element;
}

function useCoreTable<TData extends RowData>({ columns, rowLink, ...options }: TableProps<TData>) {
  const _columns = useMemo<TableProps<TData>['columns']>(() => {
    if (!rowLink) return columns;

    return [
      {
        id: internalRowLink,
        header: '',
        cell: ({ row }) =>
          cloneElement(rowLink(row.original), {
            'data-column-id': internalRowLink,
          }),
      },
      ...columns,
    ];
  }, [columns, rowLink]);

  return useTanStackTable({
    features: marbleTableFeatures,
    manualSorting: true,
    manualFiltering: true,
    columns: _columns,
    ...options,
  });
}

export function useVirtualTable<TData extends RowData>(options: TableProps<TData>) {
  const table = useCoreTable(options);

  const scrollElementRef = useRef<HTMLTableElement>(null);

  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => scrollElementRef.current,
    count: rows.length,
    estimateSize: () => 64,
    overscan: 10,
  });

  const { getTotalSize, getVirtualItems } = rowVirtualizer;
  const virtualRows = getVirtualItems();
  const totalSize = getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  return {
    table,
    getBodyProps: () => {
      return { paddingTop, paddingBottom };
    },
    getContainerProps: () => {
      return { scrollElementRef, withRowLink: options?.rowLink !== undefined };
    },
    isEmpty: rows.length === 0,
    rows: virtualRows.map(
      (virtualRow) =>
        // Safe to cast as virtualRows is built from rows
        rows[virtualRow.index] as Row<MarbleTableFeatures, TData>,
    ),
    scrollToTop: () => {
      rowVirtualizer.scrollToIndex(0);
    },
  };
}

export function useTable<TData extends RowData>(options: TableProps<TData>) {
  const table = useCoreTable(options);

  const scrollElementRef = useRef<HTMLTableElement>(null);

  const { rows } = table.getRowModel();

  return {
    table,
    getBodyProps: () => {
      return { paddingTop: 0, paddingBottom: 0 };
    },
    getContainerProps: () => {
      return { scrollElementRef, withRowLink: options?.rowLink !== undefined };
    },
    rows,
  };
}

function Body({
  paddingTop,
  paddingBottom,
  children,
}: {
  paddingTop: number;
  paddingBottom: number;
  children: React.ReactNode;
}) {
  return (
    <tbody>
      {paddingTop > 0 ? (
        <tr>
          <td style={{ height: `${paddingTop}px` }} />
        </tr>
      ) : null}
      {children}
      {paddingBottom > 0 ? (
        <tr>
          <td style={{ height: `${paddingBottom}px` }} />
        </tr>
      ) : null}
    </tbody>
  );
}

// biome-ignore lint/suspicious/noRedeclare: <TBD>
function Row<TData extends RowData>({
  row,
  className,
  onClick,
  ...props
}: Omit<React.ComponentProps<'tr'>, 'children'> & { row: Row<MarbleTableFeatures, TData> }) {
  const withRowLink = useContext(WithRowLinkContext);

  return (
    <tr
      onClick={(e) => {
        if (withRowLink) {
          const rowLink = e.currentTarget.querySelector('[data-column-id="__internal-row-link"]');
          if (rowLink && rowLink !== e.target && rowLink instanceof HTMLAnchorElement) {
            // Use dispatchEvent rather than click() to keep base event properties (ie: meta key pressed)
            rowLink.dispatchEvent(new MouseEvent(e.type, e.nativeEvent));
          }
        }
        onClick?.(e);
      }}
      className={clsx(
        'group/row',
        'h-12 even:bg-surface-row dark:even:bg-transparent',
        'odd:hover:bg-surface-row-hover even:hover:bg-surface-row-hover',
        withRowLink && 'focus-within:bg-surface-row-hover cursor-pointer group/row-link',
        className,
      )}
      {...props}
    >
      {row.getVisibleCells().map((cell, index) => {
        const context = cell.getContext();
        if (context.column.id === internalRowLink) {
          return <td key={`${cell.id}-${index}`}>{flexRender(cell.column.columnDef.cell, context)}</td>;
        }
        return (
          <td key={`${cell.id}-${index}`} className="text-s w-full truncate px-md font-normal">
            {flexRender(cell.column.columnDef.cell, context)}
          </td>
        );
      })}
    </tr>
  );
}

function DefaultTable<TData extends RowData>({
  table,
  getBodyProps,
  rows,
  getContainerProps,
}: ReturnType<typeof useTable<TData>>) {
  return (
    <Table.Container {...getContainerProps()} className="bg-surface-card max-h-96">
      <Table.Header headerGroups={table.getHeaderGroups()} />
      <Table.Body {...getBodyProps()}>
        {rows.map((row) => (
          <Table.Row key={row.id} row={row} />
        ))}
      </Table.Body>
    </Table.Container>
  );
}

export const Table = {
  Container: TableContainer,
  TH: TableTH,
  Header,
  Body,
  Row,
  Default: DefaultTable,
};
